import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FunnelStep, { OptionButton, StepInput, StepTextarea } from "@/components/funnel/FunnelStep";
import FunnelProgress from "@/components/funnel/FunnelProgress";
import { CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Resend } from 'resend';

interface FunnelData {
  goal: string;
  age: string;
  gender: string;
  occupation: string;
  motivation: string;
  continuePaid: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

const Funnel = () => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [data, setData] = useState<FunnelData>({
    goal: "",
    age: "",
    gender: "",
    occupation: "",
    motivation: "",
    continuePaid: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: ""
  });

  const totalSteps = 8;

  const updateData = (field: keyof FunnelData, value: string) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      // Scroll to top of funnel section
      setTimeout(() => {
        document.getElementById('funnel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      // Scroll to top of funnel section
      setTimeout(() => {
        document.getElementById('funnel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1: return data.goal !== "";
      case 2: return data.age !== "";
      case 3: return data.gender !== "";
      case 4: return data.occupation.trim() !== "";
      case 5: return data.motivation.trim() !== "";
      case 6: return data.continuePaid !== "";
      case 7: return data.firstName.trim() !== "" && data.lastName.trim() !== "";
      case 8: return data.phone.trim() !== "" && data.email.trim() !== "" && data.email.includes("@");
      default: return false;
    }
  };

  const handleSubmit = async () => {
    try {
      const res = await fetch("/api/funnel-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const result = await res.json();
      if (result.success) {
        setIsSubmitted(true);
      } else {
        alert("Failed to submit. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting funnel:", err);
      alert("Something went wrong. Please try again.");
    }
  };

  if (data.continuePaid === "no") {
    return (
      <section className="py-20 bg-background" id="funnel">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <div className="text-6xl">😢</div>
            <h2 className="text-3xl font-black">{t('funnel.cancelled.title')}</h2>
            <p className="text-lg text-muted-foreground">
              {t('funnel.cancelled.message')}
            </p>
            <Button 
              onClick={() => {
                setData(prev => ({ ...prev, continuePaid: "" }));
                setCurrentStep(6);
              }}
              variant="outline"
              className="mt-4"
            >
              {t('funnel.cancelled.button')}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (isSubmitted) {
    return (
      <section className="py-20 bg-background" id="funnel">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-6 animate-fade-in">
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto" />
            <h2 className="text-4xl font-black gradient-text">{t('funnel.success.title')}</h2>
            <p className="text-lg text-muted-foreground">
              {t('funnel.success.message').replace('{name}', data.firstName)}
            </p>
            <div className="card-premium p-6 text-left space-y-2">
              <h3 className="font-bold text-lg">{t('funnel.success.nextSteps')}</h3>
              <ul className="space-y-1 text-muted-foreground">
                {t('funnel.success.steps').map((step: string, index: number) => (
                  <li key={index}>• {step}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-background" id="funnel">
      <div className="container mx-auto px-4">
        <div className="min-h-[70vh] flex flex-col justify-center">
          <FunnelProgress currentStep={currentStep} totalSteps={totalSteps} />
          <div className="flex-1 flex items-center justify-center py-8">
            <div className="w-full max-w-2xl mx-auto">
        
        {currentStep === 1 && (
          <FunnelStep
            title={t('funnel.step1.title')}
            subtitle={t('funnel.step1.subtitle')}
            onNext={nextStep}
            onPrev={() => {
              // Scroll back to hero section when going back from first step
              document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
            }}
            nextDisabled={!isStepValid()}
          >
            <div className="grid gap-4">
              {t('funnel.step1.options').map((goal: string) => (
                <OptionButton
                  key={goal}
                  selected={data.goal === goal}
                  onClick={() => updateData("goal", goal)}
                >
                  <span className="font-medium">{goal}</span>
                </OptionButton>
              ))}
            </div>
          </FunnelStep>
        )}

        {currentStep === 2 && (
          <FunnelStep
            title={t('funnel.step2.title')}
            onNext={nextStep}
            onPrev={prevStep}
            nextDisabled={!isStepValid()}
          >
            <div className="grid gap-4">
              {t('funnel.step2.options').map((age: string) => (
                <OptionButton
                  key={age}
                  selected={data.age === age}
                  onClick={() => updateData("age", age)}
                >
                  <span className="font-medium">{age}</span>
                </OptionButton>
              ))}
            </div>
          </FunnelStep>
        )}

        {currentStep === 3 && (
          <FunnelStep
            title={t('funnel.step3.title')}
            onNext={nextStep}
            onPrev={prevStep}
            nextDisabled={!isStepValid()}
          >
            <div className="grid gap-4">
              {t('funnel.step3.options').map((gender: string) => (
                <OptionButton
                  key={gender}
                  selected={data.gender === gender}
                  onClick={() => updateData("gender", gender)}
                >
                  <span className="font-medium">{gender}</span>
                </OptionButton>
              ))}
            </div>
          </FunnelStep>
        )}

        {currentStep === 4 && (
          <FunnelStep
            title={t('funnel.step4.title')}
            onNext={nextStep}
            onPrev={prevStep}
            nextDisabled={!isStepValid()}
          >
            <StepInput
              label={t('funnel.step4.label')}
              value={data.occupation}
              onChange={(value) => updateData("occupation", value)}
              placeholder={t('funnel.step4.placeholder')}
              required
            />
          </FunnelStep>
        )}

        {currentStep === 5 && (
          <FunnelStep
            title={t('funnel.step5.title')}
            subtitle={t('funnel.step5.subtitle')}
            onNext={nextStep}
            onPrev={prevStep}
            nextDisabled={!isStepValid()}
          >
            <StepTextarea
              label={t('funnel.step5.label')}
              value={data.motivation}
              onChange={(value) => updateData("motivation", value)}
              placeholder={t('funnel.step5.placeholder')}
              required
            />
          </FunnelStep>
        )}

        {currentStep === 6 && (
          <FunnelStep
            title={t('funnel.step6.title')}
            subtitle={t('funnel.step6.subtitle')}
            onNext={nextStep}
            onPrev={prevStep}
            nextDisabled={!isStepValid()}
          >
            <div className="card-premium p-6 mb-6">
              <h3 className="font-bold text-lg mb-3">{t('funnel.step6.included')}</h3>
              <ul className="space-y-2 text-muted-foreground">
                {t('funnel.step6.features').map((feature: string, index: number) => (
                  <li key={index}>• {feature}</li>
                ))}
              </ul>
            </div>
            
            <div className="grid gap-4">
              <OptionButton
                selected={data.continuePaid === "yes"}
                onClick={() => updateData("continuePaid", "yes")}
              >
                <div>
                  <div className="font-medium">{t('funnel.step6.options.yes.label')}</div>
                  <div className="text-sm text-muted-foreground mt-1">{t('funnel.step6.options.yes.desc')}</div>
                </div>
              </OptionButton>
              <OptionButton
                selected={data.continuePaid === "no"}
                onClick={() => updateData("continuePaid", "no")}
              >
                <div>
                  <div className="font-medium">{t('funnel.step6.options.no.label')}</div>
                  <div className="text-sm text-muted-foreground mt-1">{t('funnel.step6.options.no.desc')}</div>
                </div>
              </OptionButton>
            </div>
          </FunnelStep>
        )}

        {currentStep === 7 && (
          <FunnelStep
            title={t('funnel.step7.title')}
            subtitle={t('funnel.step7.subtitle')}
            onNext={nextStep}
            onPrev={prevStep}
            nextDisabled={!isStepValid()}
          >
            <div className="grid md:grid-cols-2 gap-4">
              <StepInput
                label={t('funnel.step7.firstName')}
                value={data.firstName}
                onChange={(value) => updateData("firstName", value)}
                placeholder={t('funnel.step7.firstNamePlaceholder')}
                required
              />
              <StepInput
                label={t('funnel.step7.lastName')}
                value={data.lastName}
                onChange={(value) => updateData("lastName", value)}
                placeholder={t('funnel.step7.lastNamePlaceholder')}
                required
              />
            </div>
          </FunnelStep>
        )}

        {currentStep === 8 && (
          <FunnelStep
            title={t('funnel.step8.title')}
            subtitle={t('funnel.step8.subtitle')}
            onNext={handleSubmit}
            onPrev={prevStep}
            nextText="Incepe Transformarea Mea"
            nextDisabled={!isStepValid()}
            isLastStep
          >
            <div className="space-y-4">
              <StepInput
                label={t('funnel.step8.phone')}
                value={data.phone}
                onChange={(value) => updateData("phone", value)}
                placeholder="075xxxxxx"
                type="tel"
                required
              />
              <StepInput
                label={t('funnel.step8.email')}
                value={data.email}
                onChange={(value) => updateData("email", value)}
                placeholder={t('funnel.step8.emailPlaceholder')}
                type="email"
                required
              />
            </div>
            
            <div className="text-center space-y-4 mt-6">
              <div className="card-premium p-4">
                <p className="text-lg font-semibold text-primary">
                  🎁 Primește Consultația Gratuită
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Completează datele și vei fi contactat pentru o sesiune gratuită de consultanță personalizată
                </p>
              </div>
              <div className="text-sm text-muted-foreground">
                {t('funnel.step8.privacy')}
              </div>
            </div>
          </FunnelStep>
        )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Funnel;